import * as _ from 'lodash';
import { useEffect, useReducer, useState } from 'react';
import { initialState, reducer } from './list-state';
import { AxiosError } from 'axios';
import { Dispatch, connect } from 'react-redux';
import { IApplicationState, AppActionCreators, IMicrositeListResponse, MicrositeActionCreators, IMicrositeLookupDataResponse, IAppState, ICreateCardBatchResponse, IMicrositeLinkedEntities, IChangeStatusResponse, IMicrositeResponse, IChangeStatusRequest, IRemoteDonorRegistrationResponse, IBulkChangeStatusResponse, BulkChangeStatusOutcome } from '@nkr/store/index';
import { IBaseResponse, ICenter, IMicrositeCounts, IMicrositeRecipientForList, micrositeListTypeMap, IMicrositeRecipientRequest } from '@nkr/models/index';
import { MicrositeEmailType, MicrositeListType, OrganType, UserGroup } from '@nkr/enums/index';
import { BulkActions } from './models';
import { toast } from 'react-toastify';
import { DropdownButton, MenuItem } from 'react-bootstrap';
import { ColumnApi, CsvExportParams, GridApi, GridReadyEvent, ProcessCellForExportParams } from 'ag-grid-community';
import { downloadFile } from '@nkr/services/download-file';
import { MicrositeSearchProvider } from './context';
import MicrositeNav from '../nav';
import CenterMicrositeList from './center-list';
import AdminMicrositeList from './admin-list';
import Search from './search';
import SendReminderEmail from './send-reminder-email';
import ResendApprovalEmail from './resend-approval-email';
import CardBatchDownload from '../card-batch-download';
import BulkSendEmail from './bulk-send-email';
import LinkedEntitiesList from './linked-entities-list';
import ChangeStatus from '../status/change-status';
import DonorRegistrationForRemotePatients from '../../donors/donor-intake/components/donor-registrations-for-remote-patients';

interface IProps {
    center: ICenter,
    centerId: number,
    activeKey: string,
    isAdmin: boolean,
    isAdminLogin: boolean,
    itemId?: number,
    counts: IMicrositeCounts,
    onTabSelect: (key: string) => void,
    onUpdateCounts: () => Promise<any>
}

interface IStateProps {
    app: IAppState
}

interface IDispatchProps {
    showLoading: (message: string) => Promise<void>,
    hideLoading: () => void,
    getMicrositeList: (coachContactId: number, centerId: number, status: number, type: number, startDate: string, endDate: string, search: string, campaign: string, listType: MicrositeListType, micrositeId: number) => Promise<IMicrositeListResponse>,
    getLookupData: (centerId: number, micrositeRecipientId: number) => Promise<IMicrositeLookupDataResponse>,
    sendEmail: (id: number, emailType: number) => Promise<IBaseResponse>,
    createCardBatchFile: () => Promise<ICreateCardBatchResponse>,
    downloadCardBatchFile: (id: number) => Promise<any>,
    bulkSendEmail: (list: { id: number, emailType: number }[]) => Promise<IBaseResponse>,
    getMicrositeLinkedEntities: (micrositeId: number) => Promise<IMicrositeLinkedEntities>,
    registrationsForRemotePatients: (donationType: number, centerId: number, firstName: string, lastName: string, lastInitial: string, statusType: number[], micrositeRecipientId: number, onlyComplete: boolean, limitStart: number, limitEnd: number) => Promise<IRemoteDonorRegistrationResponse>,
    changeStatus: (micrositeId: number, request: IChangeStatusRequest) => Promise<IChangeStatusResponse>,
    bulkChangeStatus: (micrositeIds: number[], request: IChangeStatusRequest) => Promise<IBulkChangeStatusResponse>,
    getMicrosite: (id: number, forCoach: boolean, confirm: boolean) => Promise<IMicrositeResponse>,
    saveMicrosite: (request: IMicrositeRecipientRequest) => Promise<IMicrositeResponse>,
    updateAssignedCoach: (micrositeId: number, coachContactId: number) => Promise<IBaseResponse>
}

type TProps = IProps & IStateProps & IDispatchProps;

const MicrositeList = (props: TProps) => {
    const [state, dispatch] = useReducer(reducer, _.cloneDeep(initialState));

    const [gridApi, setGridApi] = useState<GridApi>(null);
    const [columnApi, setColumnApi] = useState<ColumnApi>(null);

    const onGridReady = (e: GridReadyEvent) => {
        setGridApi(e.api);
        setColumnApi(e.columnApi);

        dispatch({
            type: 'setGrid',
            payload: {
                gridApi: e.api,
                columnApi: e.columnApi
            }
        });
    };

    useEffect(() => {
        getData(false);
    }, []);

    const getListType = () => {
        let item = micrositeListTypeMap.find(x => x.url == props.activeKey.toLowerCase());

        if (item != null) {
            return item.type;
        }

        return MicrositeListType.PendingPatient;
    };

    const getData = (updateCounts: boolean, mDataCompleted: boolean = false) => {
        let queryParams = new URLSearchParams(window.location.search);

        if (mDataCompleted == true) {
            let newHref = window.location.origin + window.location.pathname;

            if (queryParams.get('msId') != null && queryParams.get('msId').length > 0) {
                let msId = parseInt(queryParams.get('msId'));
                newHref = newHref + "?msId=" + msId;

                window.location.href = newHref;
            }
        }

        let filters = _.cloneDeep(state.search);

        let centerId = props.isAdmin ? filters.center : props.centerId;

        if (props.app.site.organType == OrganType.Liver) {
            filters.type = 1;
        }

        if (queryParams.get('center')) {
            centerId = parseInt(queryParams.get('center'));

            filters.center = centerId;
        }

        props.showLoading('Getting list of microsites, one moment please...').then(async () => {
            if (updateCounts) {
                await props.onUpdateCounts();
            }

            let msId = 0;
            let missingTransplantData = false;

            if (queryParams.get('msId') != null && queryParams.get('msId').length > 0) {
                msId = parseInt(queryParams.get('msId'));
            }

            if (queryParams.get('mData') != null && queryParams.get('mData').length > 0) {
                missingTransplantData = parseInt(queryParams.get('mData')) == 1;
            }

            Promise.all([
                props.getMicrositeList(filters.coachContactId, centerId, filters.status, filters.type, filters.startDate, filters.endDate, filters.search, filters.campaign, getListType(), msId),
                props.getLookupData(props.centerId, 0)
            ]).then(([msListResponse, lookupDataResponse]) => {
                if (msListResponse.data.success && lookupDataResponse.data.success) {
                    let list = msListResponse.data.results.list.map(x => ({ ...x, isSelected: false }));
                    let { statusList, genderList, stateList, donorOriginList, emailTypeList, dashRegistrationList, coordinatorList, coachList, micrositeTypeList, eventTypeList, centerList } = lookupDataResponse.data.results;
                    dispatch({
                        type: 'afterGetData',
                        payload: {
                            list,
                            lookupData: {
                                centerList,
                                statusList,
                                genderList,
                                stateList,
                                donorOriginList,
                                emailTypeList,
                                dashRegistrationList,
                                coordinatorList,
                                coachList,
                                micrositeTypeList,
                                eventTypeList,
                            },
                            search: filters
                        }
                    })

                    if (msId > 0) {
                        let data = list.find(x => x.id == msId);

                        dispatch({
                            type: 'changeStatus',
                            payload: {
                                show: true,
                                data,
                                missingData: missingTransplantData,
                            }
                        });
                    }
                }
            });
        });
    };

    const getCenterList = () => {
        return _.orderBy(state.centerList, (item) => {
            return item.alias
        }, 'asc');
    };

    const getStatusList = () => {
        return _.orderBy(state.statusList, (item) => {
            return item.id
        }, 'asc');
    };

    const setUrlParameters = () => {
        let { center } = state.search;

        let paramList = [];

        // if (center > 0) {
        paramList.push(encodeURI('center=' + center));
        // }

        // if (statusList.active) {
        //     paramList.push(encodeURI('status[]=active'));
        // }
        // if (statusList.inactive) {
        //     paramList.push(encodeURI('status[]=inactive'));
        // }

        // if (statusList.deleted) {
        //     paramList.push(encodeURI('status[]=deleted'));
        // }

        // if (statusList.donated) {
        //     paramList.push(encodeURI('status[]=donated'));
        // }

        // if (typeList.sv) {
        //     paramList.push(encodeURI('type[]=sv'));
        // }

        // if (typeList.fv) {
        //     paramList.push(encodeURI('type[]=fv'));
        // }

        // if (typeList.bridge) {
        //     paramList.push(encodeURI('type[]=bridge'));
        // }

        // if (typeList.ndd) {
        //     paramList.push(encodeURI('type[]=ndd'));
        // }

        // if (typeList.dd) {
        //     paramList.push(encodeURI('type[]=dd'));
        // }

        // if (typeList.paired) {
        //     paramList.push(encodeURI('type[]=paired'));
        // }

        // if (typeList.remote) {
        //     paramList.push(encodeURI('type[]=remote'));
        // }

        // if (fullName != '') {
        //     paramList.push(encodeURI('fullName=' + fullName));
        // }

        // if (alias != '') {
        //     paramList.push(encodeURI('alias=' + alias));
        // }

        // if (email != '') {
        //     paramList.push(encodeURI('email=' + email));
        // }

        // paramList.push(encodeURI('remote=' + remote));

        // if (dob != null) {
        //     paramList.push(encodeURI('dob=' + dob));
        // }

        // if (donatedStartDate != '') {
        //     paramList.push('donatedStartDate=' + donatedStartDate);
        // }

        // if (donatedEndDate != '') {
        //     paramList.push('donatedEndDate=' + donatedEndDate);
        // }

        // paramList.push('did=' + donorId);

        let params = paramList.join('&');

        let item = micrositeListTypeMap.find(x => x.url == props.activeKey.toLowerCase());

        let newUrl = props.isAdmin == false ?
            '/center/' + props.centerId + '/microsite/' + item.url + '?' + params
            :
            '/admin/microsite/' + item.url + '?' + params
            ;

        window.history.pushState({}, '', newUrl);
    };

    const handleSearchChange = (property: string, value: any) => {
        let search = _.cloneDeep(state.search);

        if (property == 'center') {
            search.center = value;
        }

        if (property == 'status') {
            search.status = value;
        }

        if (property == 'type') {
            search.type = value;
        }

        if (property == 'startDate') {
            search.startDate = value;
        }

        if (property == 'endDate') {
            search.endDate = value;
        }

        if (property == 'search') {
            search.search = value;
        }

        if (property == 'campaign') {
            search.campaign = value;
        }

        if (property == 'coachContactId') {
            search.coachContactId = value;
        }

        dispatch({
            type: 'setSearch',
            payload: search
        });
    };

    const handleSearch = () => {
        let { coachContactId, status, type, startDate, endDate, search, center, campaign } = state.search;
        let centerId = props.isAdmin ? center : props.centerId;

        props.showLoading('Updating list, one moment please...').then(() => {
            props.getMicrositeList(coachContactId, centerId, status, type, startDate, endDate, search, campaign, getListType(), 0)
                .then(response => {
                    let list = response.data.results.list.map(x => ({ ...x, isSelected: false }));

                    dispatch({
                        type: 'setList',
                        payload: list
                    });
                })
                .finally(() => {
                    setUrlParameters();
                    props.hideLoading();
                });
        });
    };

    const handleResetSearch = () => {
        dispatch({
            type: 'setSearch',
            payload: {
                coachContactId: -1,
                center: 0,
                status: -1,
                type: -1,
                startDate: '',
                endDate: '',
                search: '',
                campaign: ''
            }
        });
    };

    const handleAdd = () => {
        let hrefPrefix = !props.isAdmin ? `/center/${props.centerId}/microsite` : `/admin/microsite`;
        window.location.href = hrefPrefix + '/registration';
    }

    const handleSelectAllRows = () => {
        let filteredList = state.list.map(x => x.id);

        let list = _.cloneDeep(state.list).map(x => {
            if (filteredList.indexOf(x.id) >= 0) {
                x.isSelected = !state.selectAllRows;
            }

            return x;
        });

        dispatch({
            type: 'setList',
            payload: list
        });

        dispatch({
            type: 'selectAllRows',
            payload: !state.selectAllRows
        });
    };

    const handleRowSelected = (id: number, isSelected: boolean) => {
        let list = _.cloneDeep(state.list).map(x => {
            if (x.id == id) {
                x.isSelected = isSelected;
            }

            return x;
        });

        dispatch({
            type: 'setList',
            payload: list
        });
    };

    const handleConfirmSendReminderEmail = (id: number) => {
        let data = state.list.find(x => x.id == id);

        dispatch({
            type: 'sendReminderEmail',
            payload: {
                show: true,
                data
            }
        });
    };

    const handleSendReminderEmail = () => {
        let id = state.sendReminderEmail.data.id;

        dispatch({
            type: 'sendReminderEmail',
            payload: {
                show: false,
                data: null
            }
        });

        props.showLoading('Sending reminder email, one moment please...').then(() => {
            props.sendEmail(id, MicrositeEmailType.RecipientReminder)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Reminder email sent successfully');
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error(<>Unable to send reminder email due to the following reason:<br />{error.message}</>);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleGetDonorRegistrationsForMicrosite = (micrositeRecipientId: number) => {
        let microsite = state.list.find(x => x.id == micrositeRecipientId);
        let { centerId, id, fullName } = microsite;

        var firstName = ''
        var lastName = ''
        var firstInitial = ''
        var statusType = [1, 2, 3, 4]
        var organType = -1
        var onlyComplete = false
        var limitStart = 1
        var limitEnd = 2000

        props.showLoading('Getting Registration information, one moment please...').then(() => {
            props.registrationsForRemotePatients(organType, centerId, firstName, lastName, firstInitial, statusType, id, onlyComplete, limitStart, limitEnd)
                .then(response => {
                    dispatch({
                        type: 'dashRegistrationForRemotePatients',
                        payload: {
                            show: true,
                            data: response.data.results,
                            microsite
                            // patientName: fullName
                        }
                    });
                })
                .catch((error: AxiosError) => {
                    toast.error(<>Unable load registration list due to the following reason:<br />{error.message}</>);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleCancelDonorRegistration = () => {
        dispatch({
            type: 'dashRegistrationForRemotePatients',
            payload: {
                show: false,
                data: null,
                microsite: null
                // patientName: ''
            }
        });
    };

    const handleCancelSendReminderEmail = () => {
        dispatch({
            type: 'sendReminderEmail',
            payload: {
                show: false,
                data: null
            }
        });
    };

    const handleCancelLinkedEntities = () => {
        dispatch({
            type: 'linkedEntitiesList',
            payload: {
                show: false,
                data: null
            }
        });
    };

    const handleConfirmResendApprovalEmail = (id: number) => {
        let data = state.list.find(x => x.id == id);

        dispatch({
            type: 'resendApprovalEmail',
            payload: {
                show: true,
                data
            }
        });
    };

    const handleCoachChange = async (micrositeId: number, coachContactId: number) => {
        try {
            let row = state.list.find(x => x.id == micrositeId);

            const response = await props.updateAssignedCoach(micrositeId, coachContactId);

            if (response.data.success) {
                dispatch({
                    type: 'updateAssignedCoach',
                    payload: {
                        micrositeId,
                        coachContactId: coachContactId > 0 ? coachContactId : 0
                    }
                });

                gridApi.applyTransaction({
                    update: [
                        { ...row, coachContactId: coachContactId > 0 ? coachContactId : 0 }
                    ]
                });
            }
        } catch (_ex) {
            toast.error("There was a problem assigning the new coach.  If this error persists, please contact support.")
        }
    }

    const handleShowLinkedEntities = (id: number) => {
        props.getMicrositeLinkedEntities(id).then(response => {
            if (response.data.success) {
                if (response.data.results.length == 0) {
                    toast.info("Linked Patients not found");

                } else {
                    dispatch({
                        type: 'linkedEntitiesList',
                        payload: {
                            show: true,
                            data: response.data.results
                        }
                    });
                }
            }
        });
    };

    const handleResendApprovalEmail = () => {
        let id = state.resendApprovalEmail.data.id;

        dispatch({
            type: 'resendApprovalEmail',
            payload: {
                show: false,
                data: null
            }
        });

        props.showLoading('Resending approval email, one moment please...').then(() => {
            props.sendEmail(id, MicrositeEmailType.RecipientApproved)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Approval email resent successfully');
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error(<>Unable to resend approval email due to the following reason:<br />{error.message}</>);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleCancelResendApprovalEmail = () => {
        dispatch({
            type: 'resendApprovalEmail',
            payload: {
                show: false,
                data: null
            }
        });
    };

    const handleCancelChangeStatus = () => {
        dispatch({
            type: 'changeStatus',
            payload: {
                show: false,
                data: null,
                missingData: false,
            }
        });
    };

    const handleStatusChanged = (recipient: IMicrositeRecipientForList, request: IChangeStatusRequest) => {
        props.showLoading('Updating microsite status, one moment please...').then(() => {
            props.changeStatus(recipient.id, request)
                .then(response => {
                    if (response.data.success) {
                        toast.success('Microsite status updated successfully.');
                        getData(true, true);
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error(<>Unable to update microsite status due to the following reason:<br />{error.response.data}</>);
                }).finally(() => {
                    props.hideLoading();
                });
        });

        dispatch({
            type: 'changeStatus',
            payload: {
                show: false,
                data: null,
                missingData: false,
            }
        });
    };

    const handleChangeStatus = (id: number) => {
        let data = state.list.find(x => x.id == id);

        dispatch({
            type: 'changeStatus',
            payload: {
                show: true,
                data,
                missingData: false
            }
        });
    };

    const handleDownloadBatchFile = (id: number) => {
        let data = state.list.find(x => x.id == id);

        props.showLoading('Getting card batch file, one moment please...').then(() => {
            props.downloadCardBatchFile(id).then(response => {
                props.hideLoading();
                downloadFile(response.data, data.cardBatchFileName, 'text/csv');
            });
        });
    };

    const handleDownloadBatchFileAfterCreate = () => {
        let { id, filename } = state.downloadCardBatchFile.data;

        dispatch({
            type: 'downloadCardBatchFile',
            payload: {
                show: false,
                data: {
                    id: 0,
                    filename: ''
                }
            }
        });

        props.showLoading('Getting card batch file, one moment please...').then(() => {
            props.downloadCardBatchFile(id).then(response => {
                downloadFile(response.data, filename, 'text/csv');
            })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleCloseDownloadCardBatchFile = () => {
        dispatch({
            type: 'downloadCardBatchFile',
            payload: {
                show: false,
                data: {
                    id: 0,
                    filename: ''
                }
            }
        });
    };

    const handleCreateCardBatchFile = () => {
        let { coachContactId, status, type, startDate, endDate, search, campaign } = state.search;

        props.showLoading('Creating card batch file, one moment please...').then(() => {
            props.createCardBatchFile().then(async response => {
                if (response.data.success) {
                    Promise.all([
                        props.getMicrositeList(coachContactId, props.centerId, status, type, startDate, endDate, search, campaign, getListType(), 0),
                        props.onUpdateCounts()
                    ])
                        .then(([listResponse]) => {
                            if (listResponse.data.success) {
                                let list = listResponse.data.results.list.map(x => ({ ...x, isSelected: false }));

                                dispatch({
                                    type: 'setList',
                                    payload: list
                                });

                                dispatch({
                                    type: 'downloadCardBatchFile',
                                    payload: {
                                        show: true,
                                        data: response.data.results
                                    }
                                });
                            }
                        })
                        .finally(() => {
                            props.hideLoading();
                        });
                }
            });
        });
    };

    const handleBulkAction = (eventKey: any) => {
        if (eventKey == BulkActions.Deactivate) {
            dispatch({
                type: 'bulkDeactivate',
                payload: {
                    show: true,
                    list: state.list.filter(x => x.isSelected)
                }
            });
        }

        if (eventKey == BulkActions.SendReminderEmail) {
            dispatch({
                type: 'bulkSendEmail',
                payload: {
                    show: true,
                    emailType: MicrositeEmailType.RecipientReminder,
                    list: state.list.filter(x => x.isSelected)
                }
            });
        }

        if (eventKey == BulkActions.ChangeStatus) {
            dispatch({
                type: 'bulkChangeStatus',
                payload: {
                    show: true,
                    list: state.list.filter(x => x.isSelected)
                }
            });
        }
    };

    const handleCancelBulkChangeStatus = () => {
        dispatch({
            type: 'bulkChangeStatus',
            payload: {
                show: false,
                list: []
            }
        });
    };

    const handleBulkStatusChanged = (request: IChangeStatusRequest) => {
        let micrositeIds = state.bulkChangeStatus.list.map(x => x.id);

        dispatch({
            type: 'bulkChangeStatus',
            payload: {
                show: false,
                list: []
            }
        });

        props.showLoading('Updating microsite statuses, one moment please...').then(() => {
            props.bulkChangeStatus(micrositeIds, request)
                .then(response => {
                    if (response.data.success) {
                        let { updatedCount, skippedCount, failedCount, items } = response.data.results;

                        toast.success(`${updatedCount} microsite(s) updated.`);

                        if (skippedCount > 0) {
                            toast.info(`${skippedCount} microsite(s) skipped because they are already active.`);
                        }

                        if (failedCount > 0) {
                            let failures = items.filter(x => x.outcome == BulkChangeStatusOutcome.Failed);

                            toast.error(
                                <>
                                    {failedCount} microsite(s) could not be updated:
                                    <br />
                                    {failures.map(f => <span key={f.micrositeId}>{f.patientName}: {f.message}<br /></span>)}
                                </>
                            );
                        }

                        getData(true, true);
                    }
                })
                .catch((error: AxiosError) => {
                    toast.error(<>Unable to update microsite statuses due to the following reason:<br />{error.response.data}</>);
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleBulkSendEmail = () => {
        let requests = state.bulkSendEmail.list.map(x => {
            return { id: x.id, emailType: MicrositeEmailType.RecipientReminder };
        });

        dispatch({
            type: 'bulkSendEmail',
            payload: {
                show: false,
                emailType: 0,
                list: []
            }
        });

        props.showLoading('Send email, one moment please...').then(() => {
            props.bulkSendEmail(requests)
                .then(response => {
                    toast.success('Emails sent successfully');
                })
                .finally(() => {
                    props.hideLoading();
                });
        });
    };

    const handleCancelBulkSendEmail = () => {
        dispatch({
            type: 'bulkSendEmail',
            payload: {
                show: false,
                emailType: 0,
                list: []
            }
        });
    };

    const handleLoadingComplete = () => {
        dispatch({
            type: 'setLoading',
            payload: false
        });
    };

    const handleExport = () => {
        let listType = micrositeListTypeMap.find(x => x.url == props.activeKey.toLowerCase());

        let excludedColumns = ['isSelected', 'rowNumber'];

        let c = state.columnApi.getAllDisplayedColumns().map(x => x.getColId()).filter(colId => excludedColumns.indexOf(colId) < 0);

        let params: CsvExportParams = {
            columnGroups: true,
            fileName: `microsites-${listType.name.toLowerCase()}.csv`,
            columnKeys: c,
            processCellCallback: (params: ProcessCellForExportParams) => {
                return params.value;
            }
        };

        state.gridApi.exportDataAsCsv(params);
    };

    const listType = getListType();
    const list = state.list;
    const centerList = getCenterList();
    const statusList = getStatusList();
    const micrositeTypeList = state.micrositeTypeList;
    const disableBulkActions = state.list.filter(x => x.isSelected).length == 0;

    return (
        <MicrositeSearchProvider value={{ coachList: state.coachList, onCoachChange: handleCoachChange }}>
            {!props.isAdmin && (
                <div style={{ width: "100%", borderLeft: "1px solid #ddd", borderRight: "1px solid #ddd", borderBottom: "1px solid #ddd", minHeight: "75vh", paddingTop: 0, paddingLeft: 10, paddingRight: 10, backgroundColor: "#FFF" }}>
                    <MicrositeNav
                        counts={props.counts}
                        activeKey={props.activeKey}
                        isAdmin={false}
                        isNkrLogin={props.app.user.groupId == UserGroup.Admin}
                        isMicrositeCoach={props.app.user.isMicrositeCoach}
                        centerId={props.centerId}
                    />

                    <Search
                        coachContactId={state.search.coachContactId}
                        center={state.search.center}
                        status={state.search.status}
                        type={state.search.type}
                        startDate={state.search.startDate}
                        endDate={state.search.endDate}
                        search={state.search.search}
                        campaign={state.search.campaign}
                        listType={listType}
                        isAdmin={false}
                        centerList={centerList}
                        statusList={statusList}
                        micrositeTypeList={micrositeTypeList}
                        canCreateCardBatch={false}
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                        onReset={handleResetSearch}
                        onAdd={handleAdd}
                        onCreateBatch={() => null}
                        onExport={handleExport}
                        centerId={props.centerId}
                    />

                    {(props.activeKey.toLowerCase() == 'stagnant-sites') &&
                        <div style={{ color: '#ba4644', marginBottom: 10 }}>
                            A Stagnant Microsite is defined as a Microsite with no DASH registrations in the past 6 months. To clear the to-do and remove the patient from this tab
                            please change their status in the Actions dropdown.
                        </div>
                    }

                    <CenterMicrositeList
                        isAdminLogin={props.isAdminLogin}
                        centerId={props.centerId}
                        listType={listType}
                        list={list}
                        isLoading={state.isLoading}
                        onSelectAllRows={handleSelectAllRows}
                        onRowSelected={handleRowSelected}
                        onSendReminderEmail={handleConfirmSendReminderEmail}
                        onGetDonorRegistrationForMSPatients={handleGetDonorRegistrationsForMicrosite}
                        onResendApprovalEmail={handleConfirmResendApprovalEmail}
                        onShowLinkedEntities={handleShowLinkedEntities}
                        onDownloadBatchFile={handleDownloadBatchFile}
                        onLoadingComplete={handleLoadingComplete}
                        onChangeStatus={handleChangeStatus}
                        onGridReady={onGridReady}
                    />

                    <div style={{ marginTop: 5, marginBottom: 5 }}>
                        <DropdownButton bsSize='sm' title='Bulk Actions' id='bulk-actions' disabled={disableBulkActions} onSelect={handleBulkAction}>
                            <MenuItem eventKey={BulkActions.SendReminderEmail}>Send Reminder Email</MenuItem>
                            <MenuItem eventKey={BulkActions.ChangeStatus}>Change Status</MenuItem>
                        </DropdownButton>
                    </div>
                </div>
            )}

            {props.isAdmin && (
                <div style={{ width: "100%" }}>
                    <MicrositeNav
                        counts={props.counts}
                        activeKey={props.activeKey}
                        isAdmin={true}
                        isNkrLogin={props.app.user.groupId == UserGroup.Admin}
                        isMicrositeCoach={props.app.user.isMicrositeCoach}
                        centerId={props.centerId}
                    />

                    <Search
                        coachContactId={state.search.coachContactId}
                        center={state.search.center}
                        status={state.search.status}
                        type={state.search.type}
                        campaign={state.search.campaign}
                        startDate={state.search.startDate}
                        endDate={state.search.endDate}
                        search={state.search.search}
                        listType={listType}
                        isAdmin={true}
                        centerList={centerList}
                        statusList={statusList}
                        micrositeTypeList={micrositeTypeList}
                        canCreateCardBatch={props.counts.pendingCardsCount > 0}
                        onChange={handleSearchChange}
                        onSearch={handleSearch}
                        onReset={handleResetSearch}
                        onAdd={handleAdd}
                        onCreateBatch={handleCreateCardBatchFile}
                        onExport={handleExport}
                    />
                    {(props.activeKey.toLowerCase() == 'stagnant-sites') &&
                        <div style={{ color: '#ba4644', marginBottom: 10 }}>
                            A Stagnant Microsite is defined as a Microsite with no DASH registrations in the past 6 months. To clear the to-do and remove the patient from this tab
                            please change their status in the Actions dropdown.
                        </div>
                    }
                    <AdminMicrositeList
                        centerId={props.centerId}
                        listType={listType}
                        list={list}
                        isLoading={state.isLoading}
                        onSelectAllRows={handleSelectAllRows}
                        onRowSelected={handleRowSelected}
                        onSendReminderEmail={handleConfirmSendReminderEmail}
                        onResendApprovalEmail={handleConfirmResendApprovalEmail}
                        onGetDonorRegistrationForMSPatients={handleGetDonorRegistrationsForMicrosite}
                        onDownloadBatchFile={handleDownloadBatchFile}
                        onLoadingComplete={handleLoadingComplete}
                        onGridReady={onGridReady}
                        onShowLinkedEntities={handleShowLinkedEntities}
                        onChangeStatus={handleChangeStatus}
                    />

                    {list.length == 500 && listType == MicrositeListType.All && <div style={{ marginTop: '10px' }}>There are more than 500 rows please limit your search criteria.</div>}

                    <div style={{ marginTop: 5, marginBottom: 5 }}>
                        <DropdownButton bsSize='sm' title='Bulk Actions' id='bulk-actions' disabled={disableBulkActions} onSelect={handleBulkAction}>
                            <MenuItem eventKey={BulkActions.SendReminderEmail}>Send Reminder Email</MenuItem>
                            <MenuItem eventKey={BulkActions.ChangeStatus}>Change Status</MenuItem>
                        </DropdownButton>
                    </div>
                </div>
            )}

            <SendReminderEmail
                show={state.sendReminderEmail.show}
                data={state.sendReminderEmail.data}
                onConfirm={handleSendReminderEmail}
                onCancel={handleCancelSendReminderEmail}
            />

            <LinkedEntitiesList
                show={state.linkedEntitiesList.show}
                data={state.linkedEntitiesList.data}
                onGridReady={onGridReady}
                onCancel={handleCancelLinkedEntities}
            />

            <ResendApprovalEmail
                show={state.resendApprovalEmail.show}
                data={state.resendApprovalEmail.data}
                onConfirm={handleResendApprovalEmail}
                onCancel={handleCancelResendApprovalEmail}
            />

            <CardBatchDownload
                show={state.downloadCardBatchFile.show}
                onDownload={handleDownloadBatchFileAfterCreate}
                onClose={handleCloseDownloadCardBatchFile}
            />

            <BulkSendEmail
                show={state.bulkSendEmail.show}
                isAdmin={props.isAdmin}
                emailType={state.bulkSendEmail.emailType}
                emailTypeList={state.emailTypeList}
                list={state.bulkSendEmail.list}
                onSubmit={handleBulkSendEmail}
                onCancel={handleCancelBulkSendEmail}
            />

            <DonorRegistrationForRemotePatients
                show={state.dashRegistrationForRemotePatients.show}
                data={state.dashRegistrationForRemotePatients.data}
                microsite={state.dashRegistrationForRemotePatients.microsite}
                // isMicrositeView={true}
                // micrositeHasCenter={true}
                // patientName={state.dashRegistrationForRemotePatients.patientName}
                onCancel={handleCancelDonorRegistration}
            />

            <ChangeStatus
                recipient={state.changeStatus.data}
                open={state.changeStatus.show}
                missingData={state.changeStatus.missingData}
                eventTypeList={state.eventTypeList}
                coordinatorList={state.coordinatorList}
                onStatusChanged={handleStatusChanged}
                onClosed={handleCancelChangeStatus}
            />

            {state.bulkChangeStatus.show &&
                <ChangeStatus
                    recipients={state.bulkChangeStatus.list}
                    open={state.bulkChangeStatus.show}
                    missingData={false}
                    eventTypeList={state.eventTypeList}
                    coordinatorList={state.coordinatorList}
                    onStatusChanged={handleStatusChanged}
                    onBulkStatusChanged={handleBulkStatusChanged}
                    onClosed={handleCancelBulkChangeStatus}
                />
            }
        </MicrositeSearchProvider>
    );
}

const mapStateToProps = (state: IApplicationState): IStateProps => {
    return {
        app: state.app
    };
}

const mapDispatchToProps = (dispatch: Dispatch<any>): IDispatchProps => {
    return {
        showLoading: (message) => {
            return dispatch(AppActionCreators.showLoadingAsync(message));
        },
        hideLoading: () => {
            return dispatch(AppActionCreators.hideLoading());
        },
        getMicrositeList: (coachContactId, centerId, status, type, startDate, endDate, search, campaign, listType, micrositeId) => {
            return dispatch(MicrositeActionCreators.getMicrositeList(coachContactId, centerId, status, type, startDate, endDate, search, campaign, listType, micrositeId));
        },
        getMicrositeLinkedEntities: (micrositeId) => {
            return dispatch(MicrositeActionCreators.getMicrositeLinkedEntities(micrositeId));
        },
        getLookupData: (centerId, micrositeRecipientId) => {
            return dispatch(MicrositeActionCreators.getLookupData(centerId, micrositeRecipientId));
        },
        sendEmail: (id, emailType) => {
            return dispatch(MicrositeActionCreators.sendEmail(id, emailType));
        },
        createCardBatchFile: () => {
            return dispatch(MicrositeActionCreators.createCardBatchFile());
        },
        downloadCardBatchFile: (id) => {
            return dispatch(MicrositeActionCreators.downloadCardBatchFile(id));
        },
        bulkSendEmail: (list) => {
            return dispatch(MicrositeActionCreators.bulkSendEmail(list));
        },
        registrationsForRemotePatients: (organType: number, centerId: number, firstName: string, lastName: string, firstInitial: string, statusType: number[], micrositeRecipientId: number, onlyComplete: boolean, limitStart: number, limitEnd: number) => {
            return dispatch(MicrositeActionCreators.registrationsForRemotePatients(organType, centerId, firstName, lastName, firstInitial, statusType, micrositeRecipientId, onlyComplete, limitStart, limitEnd));
        },
        bulkChangeStatus: (micrositeIds, request) => {
            return dispatch(MicrositeActionCreators.bulkChangeStatus(micrositeIds, request));
        },
        changeStatus: (micrositeId, request) => {
            return dispatch(MicrositeActionCreators.changeStatus(micrositeId, request));
        },
        getMicrosite: (id, forCoach, confirm) => {
            return dispatch(MicrositeActionCreators.getMicrosite(id, forCoach, confirm));
        },
        saveMicrosite: (request) => {
            return dispatch(MicrositeActionCreators.saveMicrosite(request));
        },
        updateAssignedCoach: (micrositeId, coachContactId) => {
            return dispatch(MicrositeActionCreators.updateAssignedCoach(micrositeId, coachContactId));
        }
    };
}

export default connect<IStateProps, IDispatchProps, IProps>(
    mapStateToProps,
    mapDispatchToProps)(MicrositeList);
